import { Static, Type } from "@fastify/type-provider-typebox";
import { AmazonAddress } from "./address";
import { PollingResponse } from "./polling";
import { SyncJobStatus } from "./sync";
import { PrepInstructionV2, ProductCondition } from "./products";
import { IneligibilityReasonList, SPAPIError } from "./errors";
import { PalletBody } from "./pallet";

export const FBA_API_VERSION = '/v2';

export enum LabelPrepPreference {
  SELLER_LABEL = "SELLER_LABEL",
  // AMAZON_LABEL_PREFERRED = "AMAZON_LABEL_PREFERRED", // @NOTE(nick) unused currently.
  AMAZON_LABEL_ONLY = "AMAZON_LABEL_ONLY",
}

export enum LabelPrepType {
  NO_LABEL = "NO_LABEL",
  SELLER_LABEL = "SELLER_LABEL",
  AMAZON_LABEL = "AMAZON_LABEL",
}

export enum FbaBatchBoxContentsProvider {
  BOXEM = "BOXEM",
  AMAZON = "AMAZON",
  SELLER = "SELLER",
}

export class InboundCartons {
  boxes: InboundCartonContents[];
}

// each box can have multiple products
export class InboundCartonContents {
  items: CartonItem[];
  width?: number;
  height?: number;
  length?: number;
  weight?: number;
}

export class CartonItem {
  batchItemId?: number; // actually required in v2, but in the v1 API it won't be present...
  quantity: number;

  // @TODO @DEPRECATED v2 remove all of these:
  sku: string;
  shipmentItemId: number;
  quantityInCase: number;
  expirationDate?: string;
}

export function getBatchStepOld(status: FbaBatchStatus | null): number {
  switch (status) {
    case FbaBatchStatus.CHOOSE_PRODUCTS:
      return 1;
    case FbaBatchStatus.CONFIRM_SHIPMENTS:
      return 2;
    case "PREP_AND_BOXEM" as any:
    case FbaBatchStatus.PREP_AND_SHIP:
      return 3;
    case null:
      // this indicates the shipment is external and has no batch.
      return -1;
  }
}

// this is a pure guess and can and will flag false positives.
// ASINs evolved from the ISBN 1970 standard (when they were 10-digits, ISBNs are now 13-digits),
// because amazon started as a book selling thing. This is why old books and books in general often
// have an ASIN that is a 10-digit number, instead of the more typical alphanumeric string most
// other products have.
// https://stackoverflow.com/a/12827734
export function stringMightBeASIN(str: string): boolean {
  return /^(B[\dA-Z]{9}|\d{9}(X|\d))$/.test(str);
}

// this is a super-set of statuses from the SPAPI and ours
// this is the only resource I could find on what the statuses that come from the SPAPI mean: https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v0-model
export enum FbaShipmentStatus {
  CREATED = "CREATED",
  CONFIRMED = "CONFIRMED", // step2 confirmed
  DELETED = "DELETED",

  CARTON_SUBMITTED = "CARTON_SUBMITTED",
  CARTON_SUBMISSION_FAILED = "CARTON_SUBMISSION_FAILED",
  CARTON_PROCESSED = "CARTON_PROCESSED",
  CARTON_PROCESSED_WITH_ERROR = "CARTON_PROCESSED_WITH_ERROR",
  TRANSPORT_SUBMITTED = "TRANSPORT_SUBMITTED",
  TRANSPORT_SUBMISSION_ERROR = "TRANSPORT_SUBMISSION_ERROR",
  TRANSPORT_ESTIMATING = "TRANSPORT_ESTIMATING",
  TRANSPORT_ESTIMATED = "TRANSPORT_ESTIMATED",
  TRANSPORT_ESTIMATED_ERROR = "TRANSPORT_ESTIMATED_ERROR",
  TRANSPORT_CONFIRMING = "TRANSPORT_CONFIRMING",
  TRANSPORT_CONFIRMING_ERROR = "TRANSPORT_CONFIRMING_ERROR",
  TRANSPORT_CONFIRMED = "TRANSPORT_CONFIRMED",
  TRANSPORT_VOIDING = "TRANSPORT_VOIDING",
  TRANSPORT_VOID_ERROR = "TRANSPORT_VOID_ERROR",
  TRANSPORT_VOIDED = "TRANSPORT_VOIDED",
  LABEL_ACQUIRED = "LABEL_ACQUIRED",
  
  // these statuses come from the SPAPI:
  UNCONFIRMED = "UNCONFIRMED",
  WORKING = "WORKING",                      // The shipment was created by the seller, but has not yet shipped.
  READY_TO_SHIP = "READY_TO_SHIP",          // The seller has printed box labels (for Small parcel shipments) or pallet labels (for Less Than Truckload shipments).
  SHIPPED = "SHIPPED", // The shipment was picked up by the carrier.
  RECEIVING = "RECEIVING", // The shipment has arrived at the fulfillment center, but not all items have been marked as received.
  CANCELLED = "CANCELLED", // declined shipping charges in step 4. SPAPI desc: The shipment was cancelled by the seller after the shipment was sent to the fulfillment center.
  DECLINED = "DECLINED", // step2 declined. SPAPI desc: The shipment was cancelled by the seller before the shipment was sent to the  fulfillment center."
  CLOSED = "CLOSED", // The shipment has arrived at the fulfillment center and all items have been marked as received.
  ERROR = "ERROR", // There was an error with the shipment and it was not processed by Amazon.
  IN_TRANSIT = "IN_TRANSIT", // The carrier has notified the fulfillment center that it is aware of the shipment.
  DELIVERED = "DELIVERED", // The shipment was delivered by the carrier to the fulfillment center.
  CHECKED_IN = "CHECKED_IN", // The shipment was checked-in at the receiving dock of the fulfillment center.
}

export function getShipmentStep(status: FbaShipmentStatus): number {
  switch (status) {
    case FbaShipmentStatus.CANCELLED:
      return -1;
    case FbaShipmentStatus.CREATED:
    case FbaShipmentStatus.CONFIRMED:
    case FbaShipmentStatus.CARTON_SUBMITTED:
    case FbaShipmentStatus.CARTON_SUBMISSION_FAILED:
    case FbaShipmentStatus.CARTON_PROCESSED:
    case FbaShipmentStatus.CARTON_PROCESSED_WITH_ERROR:
      return 3;
    case FbaShipmentStatus.TRANSPORT_SUBMITTED:
    case FbaShipmentStatus.TRANSPORT_SUBMISSION_ERROR:
    case FbaShipmentStatus.TRANSPORT_ESTIMATING:
    case FbaShipmentStatus.TRANSPORT_ESTIMATED:
    case FbaShipmentStatus.TRANSPORT_ESTIMATED_ERROR:
    case FbaShipmentStatus.TRANSPORT_CONFIRMING:
    case FbaShipmentStatus.TRANSPORT_CONFIRMING_ERROR:
    case FbaShipmentStatus.TRANSPORT_CONFIRMED:
    case FbaShipmentStatus.TRANSPORT_VOIDING:
    case FbaShipmentStatus.TRANSPORT_VOID_ERROR:
    case FbaShipmentStatus.TRANSPORT_VOIDED:
    case FbaShipmentStatus.LABEL_ACQUIRED:
      return 4;
    case FbaShipmentStatus.SHIPPED:
    case FbaShipmentStatus.IN_TRANSIT:
    case FbaShipmentStatus.DELIVERED:
    case FbaShipmentStatus.CHECKED_IN:
    case FbaShipmentStatus.WORKING:
      return 5;
  }
}

export enum PackingType {
  INDIVIDUAL = "INDIVIDUAL",
  CASE_PACKED = "CASE_PACKED",
}

export enum BoxContentsProvider {
  Boxem = "Boxem",
  Seller = "Seller",
  Barcode2D = "Barcode2D",
}

export enum InboundPackageStatus {
  SHIPPED = "SHIPPED",
  RECEIVING = "RECEIVING",
  DELETED = "DELETED",
  CLOSED = "CLOSED",
  IN_TRANSIT = "IN_TRANSIT",
  DELIVERED = "DELIVERED",
  CHECKED_IN = "CHECKED_IN",
}

export enum FulfillmentType {
  FBA = "FBA",
  MFN = "MFN",
  FBM = "MFN"
}

export enum TransportType {
  SPD = "SPD",
  LTL = "LTL",
  FTL = "FTL",
}

export enum AutoFillListPriceFrom {
  BUY_BOX = "BUY_BOX",
  HIGHEST_FBA_OFFER = "HIGHEST_FBA_OFFER",
  LOWEST_FBA_OFFER = "LOWEST_FBA_OFFER"
}

export enum AutoFillListPriceOperation {
  INCREASE = "INCREASE",
  DECREASE = "DECREASE"
}

export enum ShippingLabelPageType {
  PackageLabel_Letter_2 = "PackageLabel_Letter_2",
  PackageLabel_Letter_4 = "PackageLabel_Letter_4",
  PackageLabel_Letter_6 = "PackageLabel_Letter_6",
  PackageLabel_Letter_6_CarrierLeft = "PackageLabel_Letter_6_CarrierLeft",
  PackageLabel_A4_2 = "PackageLabel_A4_2",
  PackageLabel_A4_4 = "PackageLabel_A4_4",
  PackageLabel_Plain_Paper = "PackageLabel_Plain_Paper",
  PackageLabel_Plain_Paper_CarrierBottom = "PackageLabel_Plain_Paper_CarrierBottom",
  PackageLabel_Thermal = "PackageLabel_Thermal",
  PackageLabel_Thermal_Unified = "PackageLabel_Thermal_Unified",
  PackageLabel_Thermal_NonPCP = "PackageLabel_Thermal_NonPCP",
  PackageLabel_Thermal_No_Carrier_Rotation = "PackageLabel_Thermal_No_Carrier_Rotation",
}

export enum ShippingLabelType {
  BARCODE_2D = "BARCODE_2D",
  UNIQUE = "UNIQUE",
  PALLET = "PALLET",
}

export enum ShippingMode {
  GROUND_SMALL_PARCEL = 'GROUND_SMALL_PARCEL', 
  FREIGHT_LTL = 'FREIGHT_LTL', 
  FREIGHT_FTL_PALLET = 'FREIGHT_FTL_PALLET', 
  FREIGHT_FTL_NONPALLET = 'FREIGHT_FTL_NONPALLET', 
  OCEAN_LCL = 'OCEAN_LCL', 
  OCEAN_FCL = 'OCEAN_FCL', 
  AIR_SMALL_PARCEL = 'AIR_SMALL_PARCEL', 
  AIR_SMALL_PARCEL_EXPRESS = 'AIR_SMALL_PARCEL_EXPRESS'
}

export enum ShippingSolution {
  AMAZON_PARTNERED_CARRIER = 'AMAZON_PARTNERED_CARRIER', 
  USE_YOUR_OWN_CARRIER = 'USE_YOUR_OWN_CARRIER'
}


// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#currency
export const FBACurrency = Type.Object({
  amount: Type.Number(),
  code: Type.String({ minLength: 3, maxLength: 3 }), // ISO-4217 currency code
});
export type FBACurrency = Static<typeof FBACurrency>;

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#incentive
export const FBAIncentive = Type.Object({
  description: Type.String({ minLength: 1, maxLength: 1024 }),
  type: Type.Union([Type.Literal("FEE"), Type.Literal("DISCOUNT")]),
  value: Type.Optional(FBACurrency),
  target: Type.Union([Type.Literal("Placement Services"), Type.Literal("Fulfillment Fee Discount")]),
})
export type FBAIncentive = Static<typeof FBAIncentive>;

// https://developer-docs.amazon.com/sp-api/docs/iso-8601
// prefer to use this over just a vanilla 'Type.String()' in order to signify intent
export const ISO8601Date = Type.String(); 
export type ISO8601Date = Static<typeof ISO8601Date>;

export const UUID = Type.String();
export type UUID = Static<typeof UUID>;

export const UnitOfWeight = Type.Union([Type.Literal("LB"),Type.Literal("KG")]);
export type UnitOfWeight = Static<typeof UnitOfWeight>;

export const Weight = Type.Object({
  unit: UnitOfWeight,
  value: Type.Number({ minimum: 0, maximum: 100_000 }),
});
export type Weight = Static<typeof Weight>;

export const UnitOfMeasurement = Type.Union([Type.Literal("IN"),Type.Literal("CM")]);
export type UnitOfMeasurement = Static<typeof UnitOfMeasurement>;

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#transportationoption
export const TransportationOption = Type.Object({
  carrier: Type.Object({
    alphaCode: Type.Optional(Type.String()), // pretty sure it's this: https://en.wikipedia.org/wiki/Standard_Carrier_Alpha_Code
    name: Type.Optional(Type.String()),
  }),
  carrierAppointment: Type.Optional(Type.Object({
    startTime: Type.String(),
    endTime: Type.String(),
  })),
  preconditions: Type.Array(Type.String()),
  quote: Type.Optional(Type.Object({
    cost: FBACurrency,
    expiration: Type.Optional(Type.String()),
    voidableUntil: Type.Optional(Type.String()),
  })),
  cost: Type.Optional(Type.Number()),
  costCurrency: Type.Optional(Type.String()),
  expiration: Type.Optional(Type.String()),
  shipmentId: Type.String(),
  shippingMode: Type.Enum(ShippingMode),
  shippingSolution: Type.Enum(ShippingSolution),
  transportationOptionId: Type.String(),
});
export type TransportationOption = Static<typeof TransportationOption>;

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#shipmentdestination
export const ShipmentDestination = Type.Object({
  address: Type.Optional(AmazonAddress), // empty if destination type is 'AMAZON_OPTIMIZED'
  destinationType: Type.Union([Type.Literal("AMAZON_OPTIMIZED"),Type.Literal("AMAZON_WAREHOUSE")]),
  warehouseId: Type.Optional(Type.String()), // empty if destination type is 'AMAZON_OPTIMIZED'
});
export type ShipmentDestination = Static<typeof ShipmentDestination>;

export interface Dimensions {
  unitOfMeasurement: UnitOfMeasurement;
  length: number;
  width: number;
  height:number;
}

export enum LabelOwner {
  AMZON = 'AMAZON',
  SELLER = 'SELLER',
  NONE = 'NONE'
}

export enum PrepOwner {
  AMZON = 'AMAZON',
  SELLER = 'SELLER',
  NONE = 'NONE'
}

export const FBAPrepInstruction = Type.Object({
  prepType: Type.String(),
  fee: Type.Optional(Type.Object({
    amount: Type.Number(),
    code: Type.String()
  })),
  prepOwner: Type.String()
});
export type FBAPrepInstruction = Static<typeof FBAPrepInstruction>;

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#item
export const FBAItem = Type.Object({
  asin: Type.String({ minLength: 1, maxLength: 10 }),
  expiration: Type.Optional(ISO8601Date),
  fnsku: Type.String({ minLength: 1, maxLength: 10 }),
  labelOwner: Type.Enum(LabelOwner),
  manufacturingLotCode: Type.Optional(Type.String()),
  msku: Type.String({ minLength: 1, maxLength: 40 }),
  prepInstructions: Type.Array(FBAPrepInstruction),
  quantity: Type.Integer({ minimum: 1, maximum: 10_000 }),
});
export type FBAItem = Static<typeof FBAItem>;

export const PlacementShipmentOptionDTO = Type.Object({
  items: Type.Optional(Type.Array(FBAItem)),
  transportationOptions: Type.Array(TransportationOption),
  fulfillmentCenter: ShipmentDestination,
  id: Type.Number(),
  shipmentId: UUID,
  estimateAmount: Type.Number(),
  estimateCurrency: Type.String(),
  boxPackageIds: Type.Array(Type.String()),
});
export type PlacementShipmentOptionDTO = Static<typeof PlacementShipmentOptionDTO>;

export const PlacementOptionDTO = Type.Object({
  status: Type.Union([Type.Literal("OFFERED"),Type.Literal("ACCEPTED"),Type.Literal("EXPIRED")]),
  discounts: Type.Array(FBAIncentive),
  expiration: Type.Optional(ISO8601Date),  
  fees: Type.Array(FBAIncentive),
  placementOptionId: Type.String(),
  shipmentOptions: Type.Array(PlacementShipmentOptionDTO),
});
export type PlacementOptionDTO = Static<typeof PlacementOptionDTO>;

export const FBAShipmentDTO = Type.Object({
  id: Type.Number(),
  createdAt: Type.String(),
  fulfillmentCenterId: Type.String(),
  destinationAddress: Type.Unknown(), // @TODO remove in v2
  fromId: Type.Number(),
  shipmentId: Type.String(),
  shipmentName: Type.String(),
  estimateAmount: Type.Optional(Type.Number()),
  estimateCurrency: Type.Optional(Type.String()),
  cartonContentError: Type.Optional(Type.String()),
  transportSubmissionError: Type.Optional(Type.String()),
  // confirmDeadline: Type.String(),
  voidDeadline: Type.Optional(Type.String()),
  status: Type.String(),
  contact: Type.Optional(Type.Object({
    id: Type.Number(),
    name: Type.String(),
    email: Type.String(),
    phone: Type.String(),
  })),
  contactName: Type.Optional(Type.String()),
  contactEmail: Type.Optional(Type.String()),
  contactPhone: Type.Optional(Type.String()),
  declaredValue: Type.Optional(Type.Number()),
  declaredValueCurrency: Type.Optional(Type.String()),
  freightClass: Type.Optional(Type.String()),
  freightReadyDate: Type.Optional(Type.String()),
  inboundShipmentId: Type.Optional(Type.String()),
  shippingLabelUrl: Type.Optional(Type.String()),
  items: Type.Array(
    Type.Object({
      id: Type.Integer(),
      asin: Type.String(),
      sku: Type.String(),
      fnSku: Type.String(),
      quantityReceived: Type.Optional(Type.Integer()),
      quantity: Type.Integer(),
      prepDetails: Type.Optional(Type.Unknown()),
      fbaItemId: Type.Integer(),
    })
  ),
  pallets: Type.Optional(Type.Array(
    Type.Object({
      id: Type.Integer(),
      weight: Type.String(),
      height: Type.String(),
      isStacked: Type.Boolean(),
    })
  )),
  boxes: Type.Optional(Type.Array(
    Type.Object({
      id: Type.Integer(),
      width: Type.Optional(Type.Number()),
      height: Type.Optional(Type.Number()),
      length: Type.Optional(Type.Number()),
      // dimensionUnit: Type.Optional(Type.String()),
      weight: Type.Optional(Type.Number()),
      // weightUnit: Type.Optional(Type.String()),
      carrierName: Type.Optional(Type.String()),
      trackingId: Type.Optional(Type.String()),
      inboundPackageStatus: Type.Optional(Type.String()),
      displayIndex: Type.Integer(),
      hasPrintedLabel: Type.Optional(Type.Boolean()),
      createdAt: Type.String(),
      shipmentItems: Type.Optional(Type.Array(
        Type.Object({
          shipmentItemId: Type.Integer(),
          quantity: Type.Integer(),
          quantityInCase: Type.Integer(),
        })
      )),
    })
  )),
  transportationOptions: Type.Optional(Type.Array(TransportationOption)),
});
export type FBAShipmentDTO = Static<typeof FBAShipmentDTO>;

//
// Create batch
//
export const CreateBatchBody = Type.Object({
  ownerUUID: Type.Optional(Type.String()),
  name: Type.String({ maxLength: 40 }),
  fromAddressId: Type.Integer(),
  contactId: Type.Optional(Type.Integer()),
  labelPrepPreference: Type.Enum(LabelPrepPreference),
  shipmentType: Type.Enum(TransportType), // @TODO rename this field to transportType
  boxContentsProvider: Type.Enum(BoxContentsProvider),
  fulfillmentType: Type.Enum(FulfillmentType),
  packingType: Type.Enum(PackingType),
  printBoxLabelsStep3: Type.Boolean(),
  autoFillListPrice: Type.Boolean(),
  autoFillListPriceOperation: Type.Enum(AutoFillListPriceOperation),
  autoFillListPriceFrom: Type.Enum(AutoFillListPriceFrom),
  autoFillListPriceAmount: Type.String(),
  showSupplierInput: Type.Boolean(),
  showBuyCostInput: Type.Boolean(),
  showDatePurchasedInput: Type.Boolean(),
  printFNSKUAfterAddingToShipment: Type.Boolean(),
  printFNSKUAfterIncreaseQuantityInShipment: Type.Boolean(),
  printFNSKUAfterAssigningToBox: Type.Boolean(),
  printFNSKUAfterIncreaseQuantityInBox: Type.Boolean(),
});
export type CreateBatchBody = Static<typeof CreateBatchBody>;

//
// Add item to batch
//
export const AddItemToBatchBody = Type.Object({
  asin: Type.String({ maxLength: 20 }),
  sku: Type.String({ maxLength: 40 }),
  name: Type.String({ maxLength: 1024 }),
  listPrice: Type.String(),
  quantity: Type.Integer(),   
  batteriesRequired: Type.Optional(Type.Boolean()),
  supplierDeclaredDgHzRegulation: Type.Optional(Type.String()),
  quantityInCase: Type.Integer({ default: 1 }),
  condition: Type.Enum(ProductCondition),
  conditionNotes: Type.Optional(Type.String()),
  buyCost: Type.Optional(Type.String()),
  supplierId: Type.Optional(Type.Integer()),
  supplier: Type.Optional(Type.String({ maxLength: 200 })),
  datePurchased: Type.Optional(Type.String()),
  expirationDate: Type.Optional(Type.String()),
  category: Type.Optional(Type.String()),
});
export type AddItemToBatchBody = Static<typeof AddItemToBatchBody>;

export const AddItemToBatchResponse = Type.Object({
  id: Type.Integer(),
  name: Type.String(),
  asin: Type.String(),
  listPrice: Type.String(),
  image: Type.String(),
  sku: Type.String(),
  fnSku: Type.String(),
  condition: Type.String(),
  conditionNotes: Type.String(),
  buyCost: Type.Number(),
  datePurchased: Type.String(),
  expirationDate: Type.String(),
  supplierId: Type.Integer(),
  quantity: Type.Integer(),
  quantityInCase: Type.Integer(),
  // destinations: Type.String(),
  listing: Type.Object({
    prepInstructions: Type.Unknown(),
  }),
});
export type AddItemToBatchResponse = Static<typeof AddItemToBatchResponse>;

//
// Patch batch item
//
export const PatchBatchItemBody = Type.Object({
  id: Type.Integer(),
  sku: Type.Optional(Type.String()),
  buyCost: Type.Optional(Type.String()),
  quantity: Type.Optional(Type.Integer({ minimum: 1 })), // if you want to remove an item, call the delete endpoint instead.
  quantityInCase: Type.Optional(Type.Integer({ minimum: 1 })),
  condition: Type.Optional(Type.String()),
  conditionNotes: Type.Optional(Type.String()),
  datePurchased: Type.Optional(Type.String()),
  expirationDate: Type.Optional(Type.String()),
  supplierId: Type.Optional(Type.Integer()),
  supplier: Type.Optional(Type.String()),
  listPrice: Type.Optional(Type.String()),
});
export type PatchBatchItemBody = Static<typeof PatchBatchItemBody>;

export const PatchBatchItemResponse = Type.Object({
  id: Type.Integer(),
  sku: Type.String(),
  buyCost: Type.Optional(Type.String()),
  quantity: Type.Integer({ minimum: 1 }),
  quantityInCase: Type.Integer({ minimum: 1 }),
  condition: Type.String(),
  conditionNotes: Type.Optional(Type.String()),
  listPrice: Type.String(),
  fnSku: Type.String(),
  datePurchased: Type.Optional(Type.String()),
  expirationDate: Type.Optional(Type.String()),
  supplierId: Type.Integer(),
});
export type PatchBatchItemResponse = Static<typeof PatchBatchItemResponse>;


//
// Create box
//
export const ShipmentCreateBoxBody = Type.Object({
  inboundPackingGroupId: Type.Optional(Type.String()), // optional because boxes can have no packing group assigned 'yet'
  // LTL flow 2 requires associate boxes for shipments
  shipmentId: Type.Optional(Type.Number()),
  boxes: Type.Array(
    Type.Object({
      // @NOTE assumed imperial, inches/lbs:
      width: Type.Optional(Type.String()),
      length: Type.Optional(Type.String()),
      height: Type.Optional(Type.String()),
      weight: Type.Optional(Type.String()),

      packingGroupId: Type.Optional(Type.String()),

      items: Type.Array(
        Type.Object({
          shipmentItemId: Type.Optional(Type.Integer()), // @TODO deprecated in v2
          sku: Type.String(), // @TODO we can probably remove this to, just refer to the batch item id
          quantityInCase: Type.Integer(), // @TODO deprecated in v2??? I don't think we ever needed this, just refer to the batch item

          // actually what we need:
          batchItemId: Type.Optional(Type.Integer()),
          quantity: Type.Integer(),
        })
      ),
    })
  ),
});
export type ShipmentCreateBoxBody = Static<typeof ShipmentCreateBoxBody>;

//
// Update box
//
export const ShipmentUpdateBoxBody = Type.Object({
  width: Type.Optional(Type.String()),
  length: Type.Optional(Type.String()),
  height: Type.Optional(Type.String()),
  weight: Type.Optional(Type.String()),
  items: Type.Array(
    Type.Object({
      batchItemId: Type.Optional(Type.Integer()),
      quantity: Type.Optional(Type.Integer()),

      // @TODO @DEPRECATED:
      sku: Type.Optional(Type.String()),
      shipmentItemId: Type.Optional(Type.Integer()),
      quantityInCase: Type.Optional(Type.Integer()),
      boxId: Type.Optional(Type.Integer()),
    })
  ),
  packingGroupId: Type.Optional(Type.String()),
  shipmentId: Type.Optional((Type.Integer())),
});
export type ShipmentUpdateBoxBody = Static<typeof ShipmentUpdateBoxBody>;

//
// Update box dimensions
//
export const ShipmentUpdateBoxDimensionsBody = Type.Object({
  width: Type.Optional(Type.Number()),
  length: Type.Optional(Type.Number()),
  height: Type.Optional(Type.Number()),
  weight: Type.Optional(Type.Number()),
});
export type ShipmentUpdateBoxDimensionsBody = Static<typeof ShipmentUpdateBoxDimensionsBody>;

//
// Submit transport
//
export const SubmitTransportBody = Type.Object({
  spInfo: Type.Optional(Type.Object({
    carrierName: Type.String(),
    boxes: Type.Array(
      Type.Object({
        boxId: Type.Integer(),
        dimensions: Type.Object({
          Width: Type.Number(),
          Height: Type.Number(),
          Length: Type.Number(),
          Unit: Type.String(),
        }),
        weight: Type.Object({
          Value: Type.Number(),
          Unit: Type.String()
        }),
      })
    ),
  })),
  ltlInfo: Type.Optional(Type.Object({
    freightClass: Type.String(),
    freightReadyDate: Type.String(),
    boxCount: Type.Integer(),
    sellerDeclaredValue: Type.Optional(Type.Object({
      Value: Type.String(),
      CurrencyCode: Type.String()
    }))
  }))
});
export type SubmitTransportBody = Static<typeof SubmitTransportBody>;

export const SubmitTransportResponse = Type.Object({
  id: Type.Integer(),
  estimateAmount: Type.String(),
  estimateCurrency: Type.String(),
  confirmDeadline: Type.String(),
  voidDeadline: Type.String(),
  status: Type.String(),
  boxes: Type.Array(
    Type.Object({
      id: Type.Number(),
      width: Type.String(),
      height: Type.String(),
      length: Type.String(),
      dimensionUnit: Type.String(),
      weight: Type.String(),
      weightUnit: Type.String(),
      trackingId: Type.String(),
      carrierName: Type.String(),
      inboundPackageStatus: Type.String(),
    })
  ),
});
export type SubmitTransportResponse = Static<typeof SubmitTransportResponse>;

//
// List shipments
//
export enum ListShipmentsSort {
  name = "name",
  type = "type",
  createdAt = "createdAt",
  totalUnits = "totalUnits",
  totalSkus = "totalSkus",
};
export const ListShipmentsQuery = Type.Object({
  page: Type.Optional(Type.Integer()),
  pageSize: Type.Optional(Type.Integer()),
  searchTerm: Type.Optional(Type.String()),
  sort: Type.Optional(Type.Enum(ListShipmentsSort)),
  ascending: Type.Optional(Type.Boolean()),
});
export type ListShipmentsQuery = Static<typeof ListShipmentsQuery>;

//
// Patch shipment
//
export const FbaShipmentPatch = Type.Object({
  shipmentName: Type.Optional(Type.String()),
});
export type FbaShipmentPatch = Static<typeof FbaShipmentPatch>;





// ----------- V2 ----------------------

export enum BoxContentInformationSource {
  BOX_CONTENT_PROVIDED = 'BOX_CONTENT_PROVIDED', // Box contents have been provided by the seller.
  MANUAL_PROCESS = 'MANUAL_PROCESS', // Box contents will be manually processed during receive. This service incurs charges.
  BARCODE_2D = 'BARCODE_2D'
}


export enum DestinationType {
  AMAZON_OPTIMIZED = 'AMAZON_OPTIMIZED',
  AMAZON_WAREHOUSE = 'AMAZON_WAREHOUSE',
}

export enum DeliveryWindowAvailabilityType {
  AVAILABLE = 'AVAILABLE',
  CONGESTED = 'CONGESTED'
}

export enum FbaBatchStatus {
  CHOOSE_PRODUCTS = "CHOOSE_PRODUCTS", // Step 1, the user is adding products to the Plan.
  CONFIRM_SHIPMENTS = "CONFIRM_SHIPMENTS", // Step 2, the user confirms each Shipment for the Plan.
  PREP_AND_SHIP = "PREP_AND_SHIP", // Step 3+

  CREATING_INBOUND_PLAN = "CREATING_INBOUND_PLAN",
  INBOUND_PLAN_CREATED = "INBOUND_PLAN_CREATED",
  INBOUND_PLAN_ERROR = "INBOUND_PLAN_ERROR",
  
  CREATING_INBOUND_PACKING_OPTION = "CREATING_INBOUND_PACKING_OPTION",
  FETCHING_INBOUND_PACKING_OPTION = "FETCHING_INBOUND_PACKING_OPTION",
  INBOUND_PACKING_OPTION_FETCHED = "INBOUND_PACKING_OPTION_FETCHED",
  INBOUND_PACKING_OPTION_ERROR = "INBOUND_PACKING_OPTION_ERROR",
  CONFIRMING_PACKING_OPTION = "CONFIRMING_PACKING_OPTION",
  
  INBOUND_BOX_SUBMITTED = "INBOUND_BOX_SUBMITTED",
  INBOUND_BOX_PROCESSED = "INBOUND_BOX_PROCESSED",
  INBOUND_BOX_ERROR = "INBOUND_BOX_ERROR",

  SUBMITTING_PREP_CATEGORIES = "SUBMITTING_PREP_CATEGORIES",
  PREP_CATEGORIES_UPDATED = "PREP_CATEGORIES_UPDATED",
  PREP_CATEGORIES_ERROR = "PREP_CATEGORIES_ERROR",

  CREATING_INBOUND_PLACEMENT_OPTION = "CREATING_INBOUND_PLACEMENT_OPTION",
  INBOUND_PLACEMENT_OPTION_CREATED = "INBOUND_PLACEMENT_OPTION_CREATED",
  CREATING_INBOUND_PLACEMENT_OPTION_ERROR = "CREATING_INBOUND_PLACEMENT_OPTION_ERROR",
  INBOUND_PLACEMENT_OPTION_ERROR = "INBOUND_PLACEMENT_OPTION_ERROR",
  INBOUND_SHIPMENT_OPTION_LOADED = "INBOUND_SHIPMENT_OPTION_LOADED",
  CONFIRMING_PLACEMENT_OPTION = "CONFIRMING_PLACEMENT_OPTION",
  INBOUND_PLACEMENT_OPTION_CONFIRMED = "INBOUND_PLACEMENT_OPTION_CONFIRMED",

  LOADING_SHIPMENT_OPTION = "LOADING_SHIPMENT_OPTION",

  CREATING_INBOUND_TRANSPORT_OPTION = "CREATING_INBOUND_TRANSPORT_OPTION",
  INBOUND_TRANSPORT_OPTION_ERROR = "INBOUND_TRANSPORT_OPTION_ERROR",
  INBOUND_TRANSPORT_OPTION_FETCHED = "INBOUND_TRANSPORT_OPTION_FETCHED",

  CONFIRMING_TRANSPORT_OPTION = "CONFIRMING_TRANSPORT_OPTION",
  INBOUND_TRANSPORT_OPTION_CONFIRM_ERROR = "INBOUND_TRANSPORT_OPTION_CONFIRM_ERROR",
  INBOUND_TRANSPORT_OPTION_CONFIRMED = "INBOUND_TRANSPORT_OPTION_CONFIRMED",

  FETCHING_BOL = "FETCHING_BOL",
  BOL_ERROR = "BOL_ERROR",
  BOL_FETCHED = "BOL_FETCHED",

  CREATING_DELIVERY_WINDOW_OPTION = "CREATING_DELIVERY_WINDOW_OPTION",
  DELIVERY_WINDOW_OPTION_CREATED = "DELIVERY_WINDOW_OPTION_CREATED",
  DELIVERY_WINDOW_OPTION_ERROR = "DELIVERY_WINDOW_OPTION_ERROR",

  CONFIRMING_DELIVERY_WINDOW_OPTION = "CONFIRMING_DELIVERY_WINDOW_OPTION",
  DELIVERY_WINDOW_OPTION_CONFIRMED = "DELIVERY_WINDOW_OPTION_CONFIRMED",

  // Confirming the selected placement and transportation options
  CONFIRMING_BATCH="CONFIRMING_BATCH",
  BATCH_PLACEMENT_OPTION_CONFIRMED = "BATCH_PLACEMENT_OPTION_CONFIRMED",
  CONFIRMING_BATCH_TRANSPORTATION_OPTION = "CONFIRMING_BATCH_TRANSPORTATION_OPTION",
  BATCH_TRANSPORTATION_OPTION_CONFIRMED = "BATCH_TRANSPORTATION_OPTION_CONFIRMED",
  BATCH_CONFIRMED = "BATCH_CONFIRMED",
  BATCH_CONFIRM_ERROR = "BATCH_CONFIRM_ERROR",

  CANCELLING_BATCH = "CANCELLING_BATCH",
  BATCH_CANCELLED = "BATCH_CANCELLED",
  BATCH_CANCEL_ERROR = "BATCH_CANCEL_ERROR",

  ERROR = "ERROR"
}

export enum FBAOptionStatus {
  ACCEPTED = "ACCEPTED",
  OFFERED = "OFFERED"
}

export const ShippingRequirements = Type.Object({
  modes: Type.Array(Type.String()),
  solution: Type.Enum(ShippingSolution)
});
export type ShippingRequirements = Static<typeof ShippingRequirements>;

export const PackingConfiguration = Type.Object({
  boxPackingMethods: Type.Optional(Type.Array(Type.Enum(BoxContentInformationSource))),
  boxRequirements: Type.Optional(Type.Object({
    weight: Type.Object({
      maximum: Type.Number(),
      minimum: Type.Number(),
      unit: Type.Enum(UnitOfWeight)
    })
  })),
  shippingRequirements: Type.Optional(Type.Array(ShippingRequirements))
});
export type PackingConfiguration = Static<typeof PackingConfiguration>;

export const PackingGroup = Type.Object({
  inboundPackingGroupId: UUID,
  items: Type.Array(FBAItem),
});
export type PackingGroup = Static<typeof PackingGroup>;

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#shippingconfiguration
export const ShippingConfiguration = Type.Object({
  shippingMode: Type.Optional(Type.Enum(ShippingMode)),
  shippingSolution: Type.Optional(Type.Enum(ShippingSolution)),
});
export type ShippingConfiguration = Static<typeof ShippingConfiguration>;

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#packingoption
export const FBAPackingOption = Type.Object({
  packingOptionId: UUID,
  expiration: Type.Optional(ISO8601Date),
  status: Type.Enum(FBAOptionStatus),
  discounts: Type.Array(FBAIncentive),
  fees: Type.Array(FBAIncentive),
  packingGroups: Type.Array(Type.String()), // array of packing group ids
  supportedShippingConfigurations: Type.Array(ShippingConfiguration),
});
export type FBAPackingOption = Static<typeof FBAPackingOption>;

export const PackingGroupDTO = Type.Object({
  inboundPackingGroupId: UUID,
  items: Type.Array(FBAItem),
});
export type PackingGroupDTO = Static<typeof PackingGroupDTO>;

export const PackingOptionDTO = Type.Object({
  packingOptionId: UUID,
  // expiration: Type.Optional(ISO8601Date),
  status: Type.Enum(FBAOptionStatus),
  // discounts: Type.Array(FBAIncentive),
  fees: Type.Array(FBAIncentive),
  packingGroups: Type.Array(PackingGroupDTO),
  // supportedShippingConfigurations: Type.Array(ShippingConfiguration),
});
export type PackingOptionDTO = Static<typeof PackingOptionDTO>;

export const FBABatchItemDTO = Type.Object({
  id: Type.Integer(),
  name: Type.String(),
  asin: Type.String(),
  listPrice: Type.String(),
  image: Type.String(),
  sku: Type.String(),
  condition: Type.String(),
  conditionNotes: Type.String(),
  buyCost: Type.String(),
  datePurchased: Type.String(),
  expirationDate: Type.String(),
  supplierId: Type.Integer(),
  quantity: Type.Integer(),
  manufacturingLotCode: Type.String(),
  labelOwner: Type.Enum(LabelOwner),
  prepOwner: Type.Enum(PrepOwner),
});
export type FBABatchItemDTO = Static<typeof FBABatchItemDTO>;

export const InboundBatch = Type.Object({
  id: Type.Integer(),
  version: Type.Optional(Type.String()),
  name: Type.String(),
  createdAt: Type.String(),
  labelPrepPreference: Type.Optional(Type.Enum(LabelPrepPreference)),
  packingType: Type.Optional(Type.Enum(PackingType)),
  shipmentType: Type.Optional(Type.Enum(TransportType)),
  fromId: Type.Integer(),
  status: Type.Enum(FbaBatchStatus),
  boxContentsProvider: Type.Optional(Type.Enum(BoxContentsProvider)),

  printBoxLabelsStep3: Type.Optional(Type.Boolean()),
  autoFillListPrice: Type.Optional(Type.Boolean()),
  autoFillListPriceOperation: Type.Optional(Type.Enum(AutoFillListPriceOperation)),
  autoFillListPriceFrom: Type.Optional(Type.Enum(AutoFillListPriceFrom)),
  autoFillListPriceAmount: Type.String(),
  showSupplierInput: Type.Optional(Type.Boolean()),
  showBuyCostInput: Type.Optional(Type.Boolean()),
  showDatePurchasedInput: Type.Optional(Type.Boolean()),
  printFNSKUAfterAddingToShipment: Type.Optional(Type.Boolean()),
  printFNSKUAfterIncreaseQuantityInShipment: Type.Optional(Type.Boolean()),
  printFNSKUAfterAssigningToBox: Type.Optional(Type.Boolean()),
  printFNSKUAfterIncreaseQuantityInBox: Type.Optional(Type.Boolean()),

  // v2:
  readyToShip: Type.Optional(Type.String()), // required in v2, always null in v1
  packingOptions: Type.Optional(Type.Array(PackingOptionDTO)),
  placementOptions: Type.Optional(Type.Array(PlacementOptionDTO)),

  // @TODO in v2, boxes are on the batch (associated with packing group and therefore shipment via packing group id)
  // @TODO convert all the below to the DTO types:
  boxes: Type.Optional(Type.Array(
    Type.Object({
      packageId: Type.Optional(Type.String()),
      shipmentId: Type.Optional(Type.Integer()),
      packingOptionId: Type.Optional(Type.String()),
      packingGroupId: Type.Optional(Type.String()),
      packingItems: Type.Optional(Type.Array(Type.Object({
        batchItemId: Type.Optional(Type.Number()),
        sku: Type.Optional(Type.String()),
        quantity: Type.Number(),
      }))),
      id: Type.Integer(),
      width: Type.Optional(Type.Number()),
      height: Type.Optional(Type.Number()),
      length: Type.Optional(Type.Number()),
      // dimensionUnit: Type.Optional(Type.String()),
      weight: Type.Optional(Type.Number()),
      // weightUnit: Type.Optional(Type.String()),
      carrierName: Type.Optional(Type.String()),
      trackingId: Type.Optional(Type.String()),
      inboundPackageStatus: Type.Optional(Type.String()),
      displayIndex: Type.Integer(),
      hasPrintedLabel: Type.Optional(Type.Boolean()), // @TODO remove (we never really used this)
      shipmentItems: Type.Optional(Type.Array( // @TODO remove (not relevant in v2)
        Type.Object({
          shipmentItemId: Type.Integer(), 
          quantity: Type.Integer(),
          quantityInCase: Type.Integer(),
        })
      )),
    })
  )),
  
  // @TODO replace with FBABatchItemDTO in v2
  items: Type.Array(
    Type.Object({
      id: Type.Integer(),
      name: Type.String(),
      asin: Type.String(),
      image: Type.String(),
      sku: Type.String(),
      listPrice: Type.String(),
      fnSku: Type.String(),
      quantity: Type.Integer(),
      quantityInCase: Type.Optional(Type.Integer()),
      condition: Type.String(), // @TODO move to enum + in the pgTable 
      conditionNotes: Type.Optional(Type.String()),
      buyCost: Type.Optional(Type.String()),
      datePurchased: Type.Optional(Type.String()),
      expirationDate: Type.Optional(Type.String()),
      supplierId: Type.Optional(Type.Number()),
      manufacturingLotCode: Type.Optional(Type.String()),

      category: Type.Optional(Type.String()),

      listing: Type.Object({ // @DEPRECATED (is on packing group)
        prepInstructions: Type.Unknown(),
      }),
      labelOwner: Type.Optional(Type.String()),
      prepOwner: Type.Optional(Type.String()),
    })
  ),
  shipments: Type.Array(
    Type.Object({
      id: Type.Number(),
      createdAt: Type.String(),
      fulfillmentCenterId: Type.String(),
      destinationAddress: Type.Unknown(), // @TODO
      // labelPrepType: Type.String(),
      fromId: Type.Number(),
      shipmentId: Type.String(),
      shipmentName: Type.String(),
      estimateAmount: Type.Optional(Type.Number()),
      estimateCurrency: Type.Optional(Type.String()),
      cartonContentError: Type.Optional(Type.String()),
      transportSubmissionError: Type.Optional(Type.String()),
      voidDeadline: Type.Optional(Type.String()),
      status: Type.String(),
      inboundShipmentId: Type.Optional(Type.String()),
      contact: Type.Optional(Type.Object({
        id: Type.Number(),
        name: Type.String(),
        email: Type.String(),
        phone: Type.String(),
      })),
      contactName: Type.Optional(Type.String()),
      contactEmail: Type.Optional(Type.String()),
      contactPhone: Type.Optional(Type.String()),
      declaredValue: Type.Optional(Type.Number()),
      declaredValueCurrency: Type.Optional(Type.String()),
      freightClass: Type.Optional(Type.String()),
      freightReadyDate: Type.Optional(Type.String()),
      items: Type.Array(
        Type.Object({
          id: Type.Integer(),
          asin: Type.String(),
          sku: Type.String(),
          fnSku: Type.String(),
          quantityReceived: Type.Optional(Type.Integer()),
          quantity: Type.Integer(),
          prepDetails: Type.Optional(Type.Unknown()),
          fbaItemId: Type.Integer(),
        })
      ),
      pallets: Type.Optional(Type.Array(
        Type.Object({
          id: Type.Integer(),
          weight: Type.String(),
          height: Type.String(),
          isStacked: Type.Boolean(),
        })
      )),
      boxes: Type.Optional(Type.Array( // @DEPRECATED in v2
        Type.Object({
          id: Type.Integer(),
          width: Type.Optional(Type.Number()),
          height: Type.Optional(Type.Number()),
          length: Type.Optional(Type.Number()),
          // dimensionUnit: Type.Optional(Type.String()),
          weight: Type.Optional(Type.Number()),
          // weightUnit: Type.Optional(Type.String()),
          carrierName: Type.Optional(Type.String()),
          trackingId: Type.Optional(Type.String()),
          inboundPackageStatus: Type.Optional(Type.String()),
          displayIndex: Type.Integer(),
          hasPrintedLabel: Type.Optional(Type.Boolean()),
          createdAt: Type.String(),
          shipmentId: Type.Optional(Type.Integer()),
          shipmentItems: Type.Array(
            Type.Object({
              shipmentItemId: Type.Integer(),
              quantity: Type.Integer(),
              quantityInCase: Type.Integer(),
            })
          ),
        })
      )),
      transportationOptions: Type.Optional(Type.Array(TransportationOption))
    })
  ),
});
export type InboundBatch = Static<typeof InboundBatch>;

export const InboundBatchForIncompleteTable = Type.Object({
  id: Type.Integer(),
  version: Type.Optional(Type.String()),
  name: Type.String(),
  createdAt: Type.String(),
  labelPrepPreference: Type.Optional(Type.Enum(LabelPrepPreference)),
  packingType: Type.Optional(Type.Enum(PackingType)),
  shipmentType: Type.Optional(Type.Enum(TransportType)),
  status: Type.Enum(FbaBatchStatus),

  // v2:
  readyToShip: Type.Optional(Type.String()), // required in v2, always null in v1
  placementOptionsStatuses: Type.Array(Type.String()),
  // @TODO replace with FBABatchItemDTO in v2
  items: Type.Array(
    Type.Object({
      id: Type.Integer(),
      name: Type.String(),
      asin: Type.String(),
      image: Type.String(),
      sku: Type.String(),
      listPrice: Type.String(),
      fnSku: Type.String(),
      quantity: Type.Integer(),
      quantityInCase: Type.Optional(Type.Integer()),
      condition: Type.String(), // @TODO move to enum + in the pgTable 
      conditionNotes: Type.Optional(Type.String()),
      buyCost: Type.Optional(Type.String()),
      datePurchased: Type.Optional(Type.String()),
      expirationDate: Type.Optional(Type.String()),
      supplierId: Type.Optional(Type.Number()),
      manufacturingLotCode: Type.Optional(Type.String()),

      category: Type.Optional(Type.String()),

      labelOwner: Type.Optional(Type.String()),
      prepOwner: Type.Optional(Type.String()),
    })
  ),
  shipments: Type.Array(
    Type.Object({
      id: Type.Number(),
      createdAt: Type.String(),
      fulfillmentCenterId: Type.String(),
      destinationAddress: Type.Unknown(), // @TODO
      // labelPrepType: Type.String(),
      fromId: Type.Number(),
      shipmentId: Type.String(),
      shipmentName: Type.String(),
      estimateAmount: Type.Optional(Type.Number()),
      estimateCurrency: Type.Optional(Type.String()),
      cartonContentError: Type.Optional(Type.String()),
      transportSubmissionError: Type.Optional(Type.String()),
      voidDeadline: Type.Optional(Type.String()),
      status: Type.String(),
      inboundShipmentId: Type.Optional(Type.String()),
      contactName: Type.Optional(Type.String()),
      contactEmail: Type.Optional(Type.String()),
      contactPhone: Type.Optional(Type.String()),
      declaredValue: Type.Optional(Type.Number()),
      declaredValueCurrency: Type.Optional(Type.String()),
      freightClass: Type.Optional(Type.String()),
      freightReadyDate: Type.Optional(Type.String()),
      items: Type.Array(
        Type.Object({
          id: Type.Integer(),
          asin: Type.String(),
          sku: Type.String(),
          fnSku: Type.String(),
          quantityReceived: Type.Optional(Type.Integer()),
          quantity: Type.Integer(),
          prepDetails: Type.Optional(Type.Unknown()),
          fbaItemId: Type.Integer(),
        })
      ),
    })
  ),
});
export type InboundBatchForIncompleteTable = Static<typeof InboundBatchForIncompleteTable>;

//
// List batches
//
export const ListInboundBatchesResponse = Type.Object({
  batches: Type.Array(InboundBatchForIncompleteTable),
  total: Type.Number(),
});
export type ListInboundBatchesResponse = Static<typeof ListInboundBatchesResponse>;
export const ListInboundBatchesQuery = Type.Object({
  page: Type.Optional(Type.Integer()),
  pageSize: Type.Optional(Type.Integer()),
  searchTerm: Type.Optional(Type.String()),
});
export type ListInboundBatchesQuery = Static<typeof ListInboundBatchesQuery>;

//
// Create batch
//
export const CreateInboundBatchBody = Type.Object({
  ownerUUID: Type.Optional(Type.String()),
  name: Type.String({ maxLength: 40 }),
  fromAddressId: Type.Integer(),
  contactId: Type.Optional(Type.Integer()),
  labelPrepPreference: Type.Enum(LabelPrepPreference),
  shipmentType: Type.Enum(TransportType), // @TODO rename this field to transportType
  boxContentsProvider: Type.Enum(BoxContentsProvider),
  fulfillmentType: Type.Enum(FulfillmentType),
  packingType: Type.Enum(PackingType),
  printBoxLabelsStep3: Type.Boolean(),
  autoFillListPrice: Type.Boolean(),
  autoFillListPriceOperation: Type.Enum(AutoFillListPriceOperation),
  autoFillListPriceFrom: Type.Enum(AutoFillListPriceFrom),
  autoFillListPriceAmount: Type.String(),
  showSupplierInput: Type.Boolean(),
  showBuyCostInput: Type.Boolean(),
  showDatePurchasedInput: Type.Boolean(),
  printFNSKUAfterAddingToShipment: Type.Boolean(),
  printFNSKUAfterIncreaseQuantityInShipment: Type.Boolean(),
  printFNSKUAfterAssigningToBox: Type.Boolean(),
  printFNSKUAfterIncreaseQuantityInBox: Type.Boolean(),
  readyToShip: Type.Optional(Type.String()),
});
export type CreateInboundBatchBody = Static<typeof CreateInboundBatchBody>;

export const CreateInboundBatchResponse = Type.Object({
  id: Type.Integer(),
  name: Type.String(),
  fromId: Type.Integer(),
  targetCountry: Type.String(),
  targetSubdivision: Type.String(),
  labelPrepPreference: Type.Enum(LabelPrepPreference),
  shipmentType: Type.Enum(TransportType), // @TODO rename this field to transportType
  boxContentsProvider: Type.String(),
  fulfillmentType: Type.Enum(FulfillmentType),
  packingType: Type.Enum(PackingType),
  printBoxLabelsStep3: Type.Boolean(),
  autoFillListPrice: Type.Boolean(),
  autoFillListPriceOperation: Type.Enum(AutoFillListPriceOperation),
  autoFillListPriceFrom: Type.Enum(AutoFillListPriceFrom),
  autoFillListPriceAmount: Type.String(),
  showSupplierInput: Type.Boolean(),
  showBuyCostInput: Type.Boolean(),
  showDatePurchasedInput: Type.Boolean(),
  printFNSKUAfterAddingToShipment: Type.Boolean(),
  printFNSKUAfterIncreaseQuantityInShipment: Type.Boolean(),
  printFNSKUAfterAssigningToBox: Type.Boolean(),
  printFNSKUAfterIncreaseQuantityInBox: Type.Boolean(),
  readyToShip: Type.String(),
  status: Type.String(), // @TODO Enum, the table itself is a varchar for some reason! Type.Enum(FbaBatchStatus),
});
export type CreateInboundBatchResponse = Static<typeof CreateInboundBatchResponse>;

//
// Update batch
//
export const UpdateInboundBatchBody = Type.Object({
  shipmentName: Type.Optional(Type.String({ maxLength: 40 })),
  fulfillmentType: Type.Optional(Type.Enum(FulfillmentType)),
  fromAddressId: Type.Optional(Type.Integer()),
  shippingMethod: Type.Optional(Type.Enum(TransportType)), // @TODO rename this field to transportType
  packingType: Type.Optional(Type.Enum(PackingType)),
  labelingPreference: Type.Optional(Type.Enum(LabelPrepPreference)), // @TODO rename this field or enum?
  boxContents: Type.Optional(Type.Enum(BoxContentsProvider)),
  printBoxLabelsStep3: Type.Optional(Type.Boolean()),
  readyToShip: Type.Optional(Type.String())
});
export type UpdateInboundBatchBody = Static<typeof UpdateInboundBatchBody>;

export const UpdateInboundBatchResponse = Type.Object({
  id: Type.Integer(),
  name: Type.String(),
  fromId: Type.Integer(),
  targetCountry: Type.String(),
  targetSubdivision: Type.String(),
  labelPrepPreference: Type.Enum(LabelPrepPreference),
  shipmentType: Type.Enum(TransportType), // @TODO rename this field to transportType
  boxContentsProvider: Type.String(),
  fulfillmentType: Type.Enum(FulfillmentType),
  packingType: Type.Enum(PackingType),
  printBoxLabelsStep3: Type.Boolean(),
  autoFillListPrice: Type.Boolean(),
  autoFillListPriceOperation: Type.Enum(AutoFillListPriceOperation),
  autoFillListPriceFrom: Type.Enum(AutoFillListPriceFrom),
  autoFillListPriceAmount: Type.String(),
  showSupplierInput: Type.Boolean(),
  showBuyCostInput: Type.Boolean(),
  showDatePurchasedInput: Type.Boolean(),
  readyToShip: Type.Optional(Type.String()),
  placementOptions: Type.Optional(Type.Array(PlacementOptionDTO)),
});
export type UpdateInboundBatchResponse = Static<typeof UpdateInboundBatchResponse>;

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#placementoption
export const FBAPlacementOption = Type.Object({
  discounts: Type.Array(FBAIncentive),
  expiration: Type.Optional(ISO8601Date),  
  fees: Type.Array(FBAIncentive),
  placementOptionId: Type.String(),
  shipmentIds: Type.Array(Type.String()),  
  status: Type.Union([Type.Literal("OFFERED"),Type.Literal("ACCEPTED"),Type.Literal("EXPIRED")]),
});
export type FBAPlacementOption = Static<typeof FBAPlacementOption>;

export const FBAInboundStatusResponse = Type.Object({
  id: Type.Number(),
  status: Type.String(),
  packingOptions: Type.Optional(Type.Array(FBAPackingOption)),
  placementOptions: Type.Optional(Type.Array(FBAPlacementOption))
});
export type FBAInboundStatusResponse = Static<typeof FBAInboundStatusResponse>;

export const FBABoxDTO = Type.Object({
  id: Type.Integer(),

  width: Type.Number(),
  height: Type.Number(),
  length: Type.Number(),

  weight: Type.Number(),

  // @TODO remove once v2 launches:
  items: Type.Optional(Type.Array(Type.Object({
    batchItemId: Type.Integer(),
    quantity: Type.Integer(),
  }))),

  displayIndex: Type.Integer(),
  trackingId: Type.Optional(Type.String()),

  // v2 only:
  shipmentId: Type.Optional(Type.Integer()),
  packageId: Type.Optional(UUID),
  packingGroupId: Type.Optional(Type.String()),
  packingItems: Type.Array(Type.Object({
    batchItemId: Type.Integer(),
    quantity: Type.Integer(),
  })),
});
export type FBABoxDTO = Static<typeof FBABoxDTO>;


export const PlacementOptionConfirmResponse = PollingResponse(Type.Object({
  placementOptionId: Type.String(), // UUID?
  batchStatus: Type.Enum(FbaBatchStatus),
  status: Type.Union([Type.Literal("OFFERED"),Type.Literal("ACCEPTED"),Type.Literal("EXPIRED")]),
}));
export type PlacementOptionConfirmResponse = Static<typeof PlacementOptionConfirmResponse>;

export const PlacementOptionConfirmSPDResponse = PollingResponse(Type.Object({
  batchStatus: Type.Enum(FbaBatchStatus),
  
  placementOptionId: Type.Optional(Type.String()), // UUID?
  status: Type.Optional(Type.Union([Type.Literal("OFFERED"),Type.Literal("ACCEPTED"),Type.Literal("EXPIRED")])),
  shipments: Type.Optional(Type.Array(FBAShipmentDTO)),
  boxes: Type.Optional(Type.Array(FBABoxDTO)),
}));
export type PlacementOptionConfirmSPDResponse = Static<typeof PlacementOptionConfirmSPDResponse>;

export const PlacementOptionConfirmLTLResponse = PollingResponse(Type.Object({
  batchStatus: Type.Enum(FbaBatchStatus),
  
  placementOptionId: Type.Optional(Type.String()), // UUID?
  status: Type.Optional(Type.Union([Type.Literal("OFFERED"),Type.Literal("ACCEPTED"),Type.Literal("EXPIRED")])),
  shipments: Type.Optional(Type.Array(FBAShipmentDTO)),
}));
export type PlacementOptionConfirmLTLResponse = Static<typeof PlacementOptionConfirmLTLResponse>;


export interface Pagination {
  nextToken?: string;
}

export type ISO3166CountryCode = string;

export interface Region {
  countryCode?: ISO3166CountryCode;
  state?: string;
  warehouseId?: string;
}



// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#box
export interface FBABox {
  boxId?: string;
  contentInformationSource?: BoxContentInformationSource;
  destinationRegion?: Region;
  dimensions?: Dimensions;
  items?: FBAItem[];
  packageId: UUID;
  quantity?: number;
  templateName?: string; //"P1 - B1",
  weight?: Weight;
} 

export interface ListPackingGroupBoxesResponse {
  boxes: FBABox[];
  pagination?: Pagination;
}

export const GenerateTransportOptionsPollingResponse = PollingResponse(Type.Array(Type.Object({
  shipmentId: UUID,
  transportationOptions: Type.Array(TransportationOption)
})));
export type GenerateTransportOptionsPollingResponse = Static<typeof GenerateTransportOptionsPollingResponse>;

export const FBAInboundResponse = PollingResponse(Type.Any());
export type FBAInboundResponse = Static<typeof FBAInboundResponse>;

// @TODO replace with FBABatchItemDTO in v2
export const FBAItemDTO = Type.Object({
  id: Type.Integer(),
  name: Type.String(),
  asin: Type.String(),
  image: Type.String(),
  sku: Type.String(),
  listPrice: Type.String(),
  fnSku: Type.String(),
  quantity: Type.Integer(),
  condition: Type.String(), // @TODO move to enum + in the pgTable 
  conditionNotes: Type.Optional(Type.String()),
  buyCost: Type.Optional(Type.String()),
  datePurchased: Type.Optional(Type.String()),
  expirationDate: Type.Optional(Type.String()),
  supplierId: Type.Optional(Type.Number()),
  category: Type.Optional(Type.String()),

  labelOwner: Type.String(),
  prepOwner: Type.String(),

  manufacturingLotCode: Type.Optional(Type.String()), // is this necessary?
  
  quantityInCase: Type.Optional(Type.Integer()), // @TODO deprecated in v2 remove
  listing: Type.Object({ // @DEPRECATED (is on packing group)
    prepInstructions: Type.Unknown(),
  }),
});
export type FBAItemDTO = Static<typeof FBAItemDTO>;

export const SubmitBoxesResponse = Type.Object({
  status: Type.Enum(FbaBatchStatus),
  boxes: Type.Optional(Type.Array(FBABoxDTO)),
  placementOptions: Type.Optional(Type.Array(PlacementOptionDTO)),
});
export type SubmitBoxesResponse = Static<typeof SubmitBoxesResponse>;

export const SubmitBoxesPollingResponse = PollingResponse(SubmitBoxesResponse);
export type SubmitBoxesPollingResponse = Static<typeof SubmitBoxesPollingResponse>;

export const SubmitItemsResponse = Type.Object({
  packingOptions: Type.Optional(Type.Array(PackingOptionDTO)),
  batchStatus: Type.Enum(FbaBatchStatus)
});
export type SubmitItemsResponse = Static<typeof SubmitItemsResponse>;

export const SubmitItemsPollingResponse = PollingResponse(SubmitItemsResponse);
export type SubmitItemsPollingResponse = Static<typeof SubmitItemsPollingResponse>;

export const GeneratePlacementOptionsResponse = Type.Object({
  status: Type.Enum(FbaBatchStatus),
  placementOptions: Type.Optional(Type.Array(PlacementOptionDTO)),
});
export type GeneratePlacementOptionsResponse = Static<typeof GeneratePlacementOptionsResponse>;

export const GeneratePlacementOptionsPollingResponse = PollingResponse(GeneratePlacementOptionsResponse);
export type GeneratePlacementOptionsPollingResponse = Static<typeof GeneratePlacementOptionsPollingResponse>;


export const FbaShipmentUpdate = Type.Object({
  shipmentName: Type.Optional(Type.String()),
  freightClass: Type.Optional(Type.String()),
  declaredValue: Type.Optional(Type.Number()),
  declaredValueCurrency: Type.Optional(Type.String()),
  readyToShip: Type.Optional(Type.String()),
  contactName: Type.Optional(Type.String()),
  contactEmail: Type.Optional(Type.String()),
  contactPhone: Type.Optional(Type.String()),
  pallets: Type.Optional(Type.Array(PalletBody))
});
export type FbaShipmentUpdate = Static<typeof FbaShipmentUpdate>;


// https://developer-docs.amazon.com/sp-api/docs/fbainboundeligibility-api-v1-reference#itemeligibilitypreview
export interface ItemElligibilityPreview {
  asin: string;
  marketplaceId?: string;
  program: "INBOUND" | "COMMINGLING";
  isEligibleForProgram: boolean;
  ineligibilityReasonList?: IneligibilityReasonList[];
}

// https://developer-docs.amazon.com/sp-api/docs/fbainboundeligibility-api-v1-reference#getitemeligibilitypreviewresponse
export interface GetItemEligibilityPreviewResponse {
  payload?: ItemElligibilityPreview;
  errors?: SPAPIError[];
}

//
// Add item to batch
//
export const AddItemToInboundBatchBody = Type.Object({
  asin: Type.String({ maxLength: 20 }),
  sku: Type.String({ maxLength: 40 }),
  name: Type.String({ maxLength: 1024 }),
  listPrice: Type.String(),
  quantity: Type.Integer(),   
  batteriesRequired: Type.Optional(Type.Boolean()),
  supplierDeclaredDgHzRegulation: Type.Optional(Type.String()),
  quantityInCase: Type.Optional(Type.Integer({ default: 1 })), // @TODO remove v2
  condition: Type.String(),
  conditionNotes: Type.Optional(Type.String()),
  buyCost: Type.Optional(Type.String()),
  supplierId: Type.Optional(Type.Integer()),
  supplier: Type.Optional(Type.String({ maxLength: 200 })),
  datePurchased: Type.Optional(Type.String()),
  expirationDate: Type.Optional(Type.String()),
  category: Type.Optional(Type.String()),  // @TODO remove v2
  manufacturingLotCode: Type.Optional(Type.String()),
  labelOwner: Type.Optional(Type.Enum(LabelOwner)),
  prepOwner: Type.Optional(Type.Enum(PrepOwner))
});
export type AddItemToInboundBatchBody = Static<typeof AddItemToInboundBatchBody>;

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#prepcategory
export const PrepCategory = Type.Union([
  Type.Literal("ADULT"),
  Type.Literal("BABY"),
  Type.Literal("FC_PROVIDED"),
  Type.Literal("FRAGILE"),
  Type.Literal("GRANULAR"),
  Type.Literal("HANGER"),
  Type.Literal("LIQUID"),
  Type.Literal("PERFORATED"),
  Type.Literal("SET"),
  Type.Literal("SHARP"),
  Type.Literal("SMALL"),
  Type.Literal("TEXTILE"),
  Type.Literal("UNKNOWN"),
  Type.Literal("NONE"),
]);
export type PrepCategory = Static<typeof PrepCategory>;

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#mskuprepdetail
export const MskuPrepDetail = Type.Object({
  allOwnersConstraint: Type.Optional(Type.Union([Type.Literal("MUST_MATCH")])),
  labelOwnerConstraint: Type.Optional(Type.Union([Type.Literal("AMAZON_ONLY"),Type.Literal("NONE_ONLY"),Type.Literal("SELLER_ONLY")])),
  msku: Type.String(),
  prepCategory: PrepCategory,
  prepOwnerConstraint: Type.Optional(Type.Union([Type.Literal("AMAZON_ONLY"),Type.Literal("NONE_ONLY"),Type.Literal("SELLER_ONLY")])),
  prepTypes: Type.Array(PrepInstructionV2), // @TODO rename type 'PrepType'
});
export type MskuPrepDetail = Static<typeof MskuPrepDetail>;

export const GetPrepDetailsResponse = Type.Object({
  mskuPrepDetails: Type.Array(MskuPrepDetail),
  invalidMSKUs: Type.Optional(Type.Array(Type.String()),)
});
export type GetPrepDetailsResponse = Static<typeof GetPrepDetailsResponse>;

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#listprepdetailsresponse
export const ListPrepDetailsResponse = Type.Object({
  mskuPrepDetails: Type.Array(MskuPrepDetail)
});
export type ListPrepDetailsResponse = Static<typeof ListPrepDetailsResponse>;

// @TODO remove in v2:
export const AddItemToInboundBatchResponse = Type.Object({
  id: Type.Integer(),
  name: Type.String(),
  asin: Type.String(),
  listPrice: Type.String(),
  image: Type.String(),
  sku: Type.String(),
  fnSku: Type.String(),
  condition: Type.String(),
  conditionNotes: Type.String(),
  buyCost: Type.Number(),
  datePurchased: Type.String(),
  expirationDate: Type.String(),
  supplierId: Type.Integer(),
  quantity: Type.Integer(),
  quantityInCase: Type.Integer(),
  manufacturingLotCode: Type.String(),
  // destinations: Type.String(),
  listing: Type.Object({
    prepInstructions: Type.Unknown(),
  }),
  labelOwner: Type.Enum(LabelOwner),
  prepOwner: Type.Enum(PrepOwner)
});
export type AddItemToInboundBatchResponse = Static<typeof AddItemToInboundBatchResponse>;

//
// Patch batch item
//
export const PatchInboundBatchItemBody = Type.Object({
  id: Type.Integer(),
  buyCost: Type.Optional(Type.String()),
  quantity: Type.Optional(Type.Integer({ minimum: 1 })), // if you want to remove an item, call the delete endpoint instead.
  conditionNotes: Type.Optional(Type.String()),
  datePurchased: Type.Optional(Type.String()),
  expirationDate: Type.Optional(Type.String()),
  supplierId: Type.Optional(Type.Integer()),
  supplier: Type.Optional(Type.String()),
  listPrice: Type.Optional(Type.String()),
  labelOwner: Type.Optional(Type.Enum(LabelOwner)),
  prepOwner: Type.Optional(Type.Enum(PrepOwner))
});
export type PatchInboundBatchItemBody = Static<typeof PatchInboundBatchItemBody>;

export const PatchInboundBatchItemResponse = Type.Object({
  id: Type.Integer(),
  buyCost: Type.Optional(Type.String()),
  quantity: Type.Integer({ minimum: 1 }),
  conditionNotes: Type.Optional(Type.String()),
  listPrice: Type.String(),
  datePurchased: Type.Optional(Type.String()),
  expirationDate: Type.Optional(Type.String()),
  supplierId: Type.Integer(),
  labelOwner: Type.Optional(Type.Enum(LabelOwner)),
  prepOwner: Type.Optional(Type.Enum(PrepOwner))
});
export type PatchInboundBatchItemResponse = Static<typeof PatchInboundBatchItemResponse>;


// @TODO support adding multiple boxes of the same content at once
export const CreateInboundBoxBody = Type.Object({
  inboundPackingGroupId: Type.Optional(Type.String()),
  boxes: Type.Array(
    Type.Object({
      quantity: Type.Optional(Type.Integer()), // number of boxes
      width: Type.Optional(Type.String()),
      length: Type.Optional(Type.String()),
      height: Type.Optional(Type.String()),
      weight: Type.Optional(Type.String()),
      items: Type.Array(
        Type.Object({
          shipmentItemId: Type.Optional(Type.Integer()),
          batchItemId: Type.Optional(Type.Integer()),
          sku: Type.String(),
          quantity: Type.Integer(), // number of units (in case packed, it is cases * units/case)
          quantityInCase: Type.Optional(Type.Integer()), // case packed, can be provided for batch
        })
      ),
    })
  ),
});
export type CreateInboundBoxBody = Static<typeof CreateInboundBoxBody>;

export const FulfillmentCenter = Type.Object({
  warehouseId: Type.String(),
  address: AmazonAddress,
  destinationType: Type.Enum(DestinationType)
});
export type FulfillmentCenter = Static<typeof FulfillmentCenter>;

export const DeliveryWindowOption = Type.Object({
  availabilityType: Type.Enum(DeliveryWindowAvailabilityType),
  startDate: Type.String(),
  endDate: Type.String(),
  validUntil: Type.String()
});

// https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#mskuprepdetailinput
export const MskuPrepDetailInput = Type.Object({
  msku: Type.String(),
  prepCategory: PrepCategory,
  prepTypes: Type.Array(PrepInstructionV2),
});
export type MskuPrepDetailInput = Static<typeof MskuPrepDetailInput>;

export const PrepCategoriesBody = Type.Object({
  operationId: Type.Optional(Type.String()),
  prepCategoriesInput: Type.Optional(Type.Array(MskuPrepDetailInput)),
});
export type PrepCategoriesBody = Static<typeof PrepCategoriesBody>;

export const PrepCategoriesResponse = Type.Object({
  operationId: Type.Optional(Type.String()),
  items: Type.Array(Type.Object({
    status: Type.Enum(SyncJobStatus),
    msku: Type.String(),
  })),
});
export type PrepCategoriesResponse = Static<typeof PrepCategoriesResponse>;

export const PrepCategoriesPollingResponse = PollingResponse(PrepCategoriesResponse);
export type PrepCategoriesPollingResponse = Static<typeof PrepCategoriesPollingResponse>;



export const ConfirmTransportationOptionResponse = Type.Array(Type.Object({
  id: Type.Optional(Type.Integer()),
  bolUrl: Type.Optional(Type.String()),
  bolS3Url: Type.Optional(Type.String()),
}));
export type ConfirmTransportationOptionResponse = Static<typeof ConfirmTransportationOptionResponse>;

export const ConfirmTransportationOptionPollingResponse = PollingResponse(ConfirmTransportationOptionResponse);
export type ConfirmTransportationOptionPollingResponse = Static<typeof ConfirmTransportationOptionPollingResponse>;


export const CancelBatchBody = Type.Object({
  operationId: Type.Optional(Type.String()),
});
export type CancelBatchBody = Static<typeof CancelBatchBody>;

export const CancelBatchResponse = Type.Object({
  operationId: Type.Optional(Type.String()),
  status: Type.Optional(Type.Enum(FbaBatchStatus)),
});
export type CancelBatchResponse = Static<typeof CancelBatchResponse>;

export const CancelBatchPollingResponse = PollingResponse(CancelBatchResponse);
export type CancelBatchPollingResponse = Static<typeof CancelBatchPollingResponse>;
